import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * bootstrap-datetimepicker (format yyyy-mm-dd, startView/minView = 2, todayBtn,
 * autoclose) fara jQuery: acelasi input.form_datetime in .input-icon, si
 * dropdown-ul .datetimepicker-days cu table.table-condensed, prev/switch/next,
 * Today in tfoot.
 *
 * -----------------------------------------------------------------------------
 * CE FACE `typed` SI DE CE EXISTA
 * -----------------------------------------------------------------------------
 *
 * Pana acum casuta era `readOnly` si se deschidea numai cu mouse-ul: cine
 * lucreaza de la tastatura ajungea in ea cu Tab si nu mai avea CE sa faca -
 * nici sa scrie o data, nici sa deschida calendarul, nici sa goleasca un camp
 * optional pe care l-a completat din greseala. Purtarea aia a ramas cea
 * IMPLICITA, fiindca ecranele care arata o data fara s-o poata schimba (fisa
 * aparatului) se sprijina pe ea.
 *
 * Cu `typed`, casuta se scrie ca oricare alta, iar calendarul se deschide si de
 * la tastatura (sageata in jos), se plimba cu sagetile, alege cu Enter si se
 * inchide cu Escape. Sagetile stanga/dreapta raman ale cursorului din text cat
 * timp campul se scrie - altfel n-ai mai putea sa te intorci peste o cifra
 * gresita fara sa mute calendarul sub tine.
 *
 * `onChange` primeste TEXTUL din casuta, asa cum e scris, nu o data curatata:
 * campul pleaca la server exact ca in portalul vechi, unde bootstrap-datepicker
 * lasa la fel ce scria omul, iar serverul e cel care spune daca e o data.
 */

/**
 * -----------------------------------------------------------------------------
 * ICONITA STA IN CASUTA SI FARA `app.css` (23 septembrie 2026)
 * -----------------------------------------------------------------------------
 *
 * Reclamatia (client, `productivitee/device_productivity` si peste tot in
 * Productivitee): iconita de calendar/ceas apare IN AFARA casutei si clicul pe
 * ea nu deschide nimic.
 *
 * Cauza banuita atunci: pozitionarea iconitei venea NUMAI din `app.css`
 * (`.input-icon i.ico { position: absolute; top: 0; ... }` si
 * `.input-icon .form-control { padding-left: 30px }`), iar foaia aia n-ar fi
 * fost pe ecranele Productivitee. Leacul a fost sa se puna aceleasi masuri
 * INLINE pe `<i>` (colt stanga-sus, absolut, latime 34, centrat) si un
 * `padding-left` pe casuta, plus `onClick` si `cursor: pointer` pe `<i>`, ca in
 * portalul vechi. Stilurile inline raman: fac pickerul de sine statator si nu
 * se bat cap in cap cu `app.css`, care spune exact acelasi lucru.
 *
 * -----------------------------------------------------------------------------
 * CAUZA ADEVARATA A ICONITEI CARE NU SE VEDE (24 septembrie 2026)
 * -----------------------------------------------------------------------------
 *
 * Banuiala de mai sus era GRESITA, si de-aia reclamatia s-a intors de doua ori
 * dupa doua livrari: `app.css` E pe toate ecranele - sta in `PORTAL_STYLES` din
 * `vite.config.ts`, pusa in `index.html`, nu prin `usePageStyles`.
 *
 * MASURAT (Edge headless, foile adevarate din `public/css`, fereastra
 * 1600x900), pe `<i class="fa fa-calendar-o ico">`:
 *
 *     getComputedStyle(i, ':before').content === 'none'
 *     document.fonts.check('900 14px "Font Awesome 5 Free"') === true
 *     iar `far fa-calendar`, pus alaturi drept martor, da un glif adevarat
 *
 * Adica fontul era incarcat si foaia era pe pagina; lipsea REGULA, fiindca
 * `fa-calendar-o` e numele din Font Awesome 4, iar `app.css` poarta Font
 * Awesome 5 Free 5.15.4, unde iconita se cheama `far fa-calendar`. Elementul
 * era desenat gol, 34x34, exact unde trebuia.
 *
 * De-aia iconita implicita e acum `far fa-calendar ico`. Cine trimite `icon`
 * de afara trimitea deja nume de Font Awesome 5 (`far fa-calendar-check ico`),
 * deci pe acolo nu era nimic de reparat.
 */
const ICON_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 34,
  height: 34,
  lineHeight: '32px',
  textAlign: 'center',
  color: '#CCC',
  zIndex: 5,
  cursor: 'pointer',
}
/** Casuta lasa loc in stanga cat iconita, si cand `app.css` nu e pe pagina. */
const INPUT_ICON_PAD: React.CSSProperties = { paddingLeft: 30 }

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function parse(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
  const d = new Date(value + 'T00:00:00')
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * -----------------------------------------------------------------------------
 * PANOUL NU MAI E `absolute`, E `fixed` SI SE ASAZA DIN MASURATOARE
 * -----------------------------------------------------------------------------
 *
 * Reclamatia (Roxana, 21 septembrie 2026): pe `productivitee/device_productivity`
 * calendarul „deschide popup-ul sub tabel si nu se vede dropdownul".
 *
 * Nu era nici z-index, nici clasa `days` (vezi mai jos) - era TAIERE. Filtrul
 * de zi urca in bara de sus (`li.tk-header-tools`, prin `headerButtons` din
 * DataTable), iar bara aia are, din `header_layout.css`:
 *
 *     #page-header .fixed-header ul.page-logo { overflow-x: auto; overflow-y: hidden }
 *
 * Regula exista ca sa nu se rupa antetul pe doua randuri cand lista urca multe
 * unelte in el, si e buna. Dar ea face din fasia de 45px un container care
 * TAIE: un panou `position: absolute` agatat de casuta e retezat la marginea
 * de jos a fasiei, si `overflow-y: hidden` nu lasa nici sa se deruleze pana la
 * el.
 *
 * MASURAT (Edge headless, foile adevarate, fereastra 1600x900):
 *
 *   inainte  panoul 234x298 la top=41 => bottom=339, iar `ul.page-logo` are
 *            clientHeight=45: 294px taiati, din 298. `scrollHeight` sarise la
 *            339 - panoul era desenat, dar dincolo de margine.
 *            `elementFromPoint` pe mijlocul panoului SI pe casuta zilei de azi
 *            intorcea un `TD` AL LISTEI (insidePopup=false, insideTable=true):
 *            exact „se vede tabelul, nu calendarul".
 *   dupa     acelasi panou 234x298 la top=40, `scrollHeight` inapoi la 50, iar
 *            `elementFromPoint` intoarce `TD.day` / `TD.day today active` din
 *            panou (insidePopup=true): calendarul sta PESTE tabel si se apasa.
 *
 * `position: fixed` scapa de taiere fiindca blocul lui de referinta nu mai e
 * inauntrul fasiei. DAR: un stramos cu `transform` ii devine bloc de referinta,
 * si Bootstrap 3 pune `transform: translate(0,0)` pe `.modal.in .modal-dialog`
 * - iar pickerul asta sta si in ferestre modale (Schedule install, Softsee).
 * MASURAT si asta: in modala, `top/left: 0` cade la (487, 31), nu la (0, 0).
 *
 * De-aia asezarea se face in DOUA treceri, nu din formula: punem panoul la
 * `0,0`, MASURAM unde a cazut coltul (= originea blocului de referinta,
 * oricare ar fi el) si scadem originea din pozitia dorita. Iese exact si in
 * antet, si in modala, fara sa stim dinainte cine e stramosul.
 *
 * Panoul RAMANE copilul lui `.input-icon` - nu se muta pe `body` printr-un
 * portal: regulile din `app.css` care il deseneaza (sageata
 * `[class*=" datetimepicker-dropdown"]:before/:after`) si probele care il
 * cauta in `container` raman cum sunt.
 */
function place(panel: HTMLElement, anchor: HTMLElement): void {
  panel.style.top = '0px'
  panel.style.left = '0px'
  const origin = panel.getBoundingClientRect()
  const box = anchor.getBoundingClientRect()
  /* Sub casuta, afara de cazul in care panoul n-ar mai incapea pana in josul
     ferestrei si deasupra e loc - atunci se rastoarna, ca in portal. */
  const below = window.innerHeight - box.bottom
  const flip = origin.height > 0 && below < origin.height && box.top > origin.height
  const wantTop = flip ? box.top - origin.height : box.bottom
  /* Si sa nu iasa prin marginea din dreapta a ferestrei. */
  const room = window.innerWidth - origin.width - 4
  const wantLeft = Math.max(4, room > 4 ? Math.min(box.left, room) : box.left)
  panel.style.top = `${Math.round(wantTop - origin.top)}px`
  panel.style.left = `${Math.round(wantLeft - origin.left)}px`
}

/**
 * Tine panoul lipit de casuta cat timp e deschis: asezarea se face inainte de
 * prima pictura (`useLayoutEffect`), si din nou la derulare sau la
 * redimensionare - in formulare casuta se muta cu pagina, iar `fixed` n-ar
 * urma-o singur. `true` la `scroll` fiindca ce se deruleaza e de obicei un
 * panou dinauntru, nu fereastra, si evenimentul ala nu urca.
 */
function useAnchored(open: boolean, panel: { current: HTMLElement | null }, anchor: { current: HTMLElement | null }, deps: unknown[]): boolean {
  const [placed, setPlaced] = useState(false)
  useLayoutEffect(() => {
    if (!open) {
      setPlaced(false)
      return
    }
    const put = () => {
      if (panel.current && anchor.current) place(panel.current, anchor.current)
    }
    put()
    setPlaced(true)
    window.addEventListener('scroll', put, true)
    window.addEventListener('resize', put)
    return () => {
      window.removeEventListener('scroll', put, true)
      window.removeEventListener('resize', put)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ...deps])
  return placed
}

export function DatePicker({ value, name, onChange, icon = 'far fa-calendar ico', id, placeholder, typed = false }: {
  value: string
  name: string
  onChange(next: string): void
  icon?: string
  id?: string
  placeholder?: string
  /** Casuta se scrie de la tastatura, iar calendarul se poate conduce cu sagetile. */
  typed?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(() => parse(value) ?? new Date())
  /** Ziua de sub tastatura cat timp calendarul e deschis (yyyy-mm-dd). */
  const [marked, setMarked] = useState('')
  const root = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const placed = useAnchored(open, panel, root, [cursor])

  useEffect(() => {
    if (!open) return
    const close = (ev: MouseEvent) => {
      if (root.current && !root.current.contains(ev.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const show = () => {
    const from = parse(value) ?? new Date()
    setCursor(from)
    setMarked(ymd(from))
    setOpen(true)
  }

  const move = (days: number) => {
    const from = parse(marked) ?? parse(value) ?? new Date()
    const next = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days)
    setMarked(ymd(next))
    setCursor(new Date(next.getFullYear(), next.getMonth(), 1))
  }

  const moveMonth = (months: number) => {
    const from = parse(marked) ?? parse(value) ?? new Date()
    const next = new Date(from.getFullYear(), from.getMonth() + months, from.getDate())
    setMarked(ymd(next))
    setCursor(new Date(next.getFullYear(), next.getMonth(), 1))
  }

  const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (ev.key === 'ArrowDown' || (!typed && (ev.key === 'Enter' || ev.key === ' '))) {
        ev.preventDefault()
        show()
      }
      return
    }
    if (ev.key === 'Escape' || ev.key === 'Tab') {
      if (ev.key === 'Escape') ev.preventDefault()
      setOpen(false)
      return
    }
    /* Stanga/dreapta raman ale cursorului din text cand campul se scrie. */
    const steps: Record<string, number> = typed
      ? { ArrowUp: -7, ArrowDown: 7 }
      : { ArrowUp: -7, ArrowDown: 7, ArrowLeft: -1, ArrowRight: 1 }
    if (steps[ev.key] !== undefined) {
      ev.preventDefault()
      move(steps[ev.key])
      return
    }
    if (ev.key === 'PageUp' || ev.key === 'PageDown') {
      ev.preventDefault()
      moveMonth(ev.key === 'PageUp' ? -1 : 1)
      return
    }
    if (ev.key === 'Enter') {
      /* Enter alege ziua, nu trimite formularul din spate. */
      ev.preventDefault()
      if (marked) onChange(marked)
      setOpen(false)
    }
  }

  const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
  const pad = start.getDay()
  const days: (Date | null)[] = []
  for (let i = 0; i < pad; i++) days.push(null)
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate()
  for (let d = 1; d <= last; d++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d))

  return (
    <div className="input-icon" ref={root} style={{ position: 'relative' }}>
      <i className={icon} style={ICON_STYLE} onClick={show} aria-hidden="true"></i>
      <input
        className="form-control form_datetime"
        type="text"
        id={id}
        name={name}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        readOnly={!typed}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={INPUT_ICON_PAD}
        onChange={typed ? (ev) => onChange(ev.target.value) : undefined}
        onKeyDown={onKeyDown}
        onClick={show}
      />
      {/*
          `days` DE PE INVELISUL DE MAI JOS NU E DECOR - EA APRINDE PANOUL.

          app.css stinge toate panourile dropdown-ului (`.datetimepicker > div
          { display: none }`) si aprinde numai pe cel al vederii curente:
          `.datetimepicker.days div.datetimepicker-days { display: block }`.
          Fara clasa aia, tabelul zilelor era desenat corect si ramanea ascuns:
          se deschidea o casuta alba de 234x2 pixeli. Asa aratau calendarele pe
          TOATE ecranele care folosesc pickerul.

          Pozitia vine din `place()` de mai sus, nu din `top: 100%`: in bara de
          sus un panou `absolute` era retezat de `overflow-y: hidden`.
      */}
      {open && (
        <div
          ref={panel}
          className="datetimepicker datetimepicker-dropdown-bottom-right dropdown-menu days"
          role="dialog"
          style={{ display: 'block', position: 'fixed', zIndex: 2010, top: 0, left: 0, visibility: placed ? undefined : 'hidden' }}
        >
          <div className="datetimepicker-days">
            <table className="table-condensed">
              <thead>
                <tr>
                  <th className="prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
                    ‹
                  </th>
                  <th className="switch" colSpan={5}>
                    {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
                  </th>
                  <th className="next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
                    ›
                  </th>
                </tr>
                <tr>
                  {DOW.map((d) => (
                    <th key={d} className="dow">
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chunk(days, 7).map((week, i) => (
                  <tr key={i}>
                    {week.map((day, j) => {
                      if (!day) return <td key={j} className="old"></td>
                      const key = ymd(day)
                      const today = key === ymd(new Date())
                      const active = key === value || key === marked
                      return (
                        <td
                          key={key}
                          className={`day${today ? ' today' : ''}${active ? ' active' : ''}`}
                          onClick={() => {
                            onChange(key)
                            setOpen(false)
                          }}
                        >
                          {day.getDate()}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th
                    colSpan={7}
                    className="today"
                    onClick={() => {
                      onChange(ymd(new Date()))
                      setOpen(false)
                    }}
                  >
                    Today
                  </th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * ORA, LANGA CALENDAR: LISTA DE ORE CARE SE DESCHIDE LA CLIC
 * -----------------------------------------------------------------------------
 *
 * In portal ora se alegea cu wickedpicker (`twentyFour: true`), care la clic
 * lasa un panou in jos. Aici nu ruleaza jQuery, deci casuta ramanea o casuta
 * alba de text din care nu iesea nimic.
 *
 * DE CE NU `input type="time"`, care ar fi fost gratis. Fiindca NU face ce s-a
 * cerut: in Chrome, un clic pe casuta pune doar cursorul intre cifre, iar ceasul
 * se deschide numai daca nimeresti iconita din colt. Omul a cerut ca la CLIC sa
 * se deschida lista de ore, ca la calendarul de langa. Casuta nativa ar fi fost
 * raspunsul bun daca intrebarea era doar "sa se poata scrie o ora"; nu e.
 *
 * Deci lista se deseneaza - dar din nimic nou: acelasi `.dropdown-menu` cu
 * `li > a > span.text` pe care le poarta si celelalte doua dropdown-uri ale
 * panoului, si aceleasi reguli din app.css. Fara biblioteca, fara dependinta.
 *
 * VALOAREA RAMANE `HH:mm`, in 24 de ore - fix textul pe care il lipeste
 * `joinDateTime` si il asteapta serverul. Casuta se si SCRIE, deci un sfert de
 * ora care nu e in lista (`21:18`) se bate la masina, ca in portal.
 */

/** Orele din lista: din `minuteStep` in `minuteStep`, de la 00:00 la 23:xx. */
function everyStep(minuteStep: number): string[] {
  const out: string[] = []
  for (let m = 0; m < 24 * 60; m += minuteStep) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
  }
  return out
}

/** Randul de la care porneste plimbarea cu sagetile: ora scrisa, sau cea mai apropiata. */
function nearest(times: string[], value: string): number {
  const exact = times.indexOf(value)
  if (exact >= 0) return exact
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!match) return 0
  const minutes = Number(match[1]) * 60 + Number(match[2])
  let best = 0
  for (let i = 1; i < times.length; i++) {
    const at = Number(times[i].slice(0, 2)) * 60 + Number(times[i].slice(3))
    const soFar = Number(times[best].slice(0, 2)) * 60 + Number(times[best].slice(3))
    if (Math.abs(at - minutes) < Math.abs(soFar - minutes)) best = i
  }
  return best
}

export function TimePicker({ value, name, onChange, id, className, icon = 'far fa-clock ico', minuteStep = 30 }: {
  value: string
  name: string
  onChange(next: string): void
  id?: string
  /** Ce se adauga la `form-control` - `input-sm` pe randul de recurenta. */
  className?: string
  icon?: string
  minuteStep?: number
}) {
  const [open, setOpen] = useState(false)
  const [marked, setMarked] = useState(0)
  const root = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const times = everyStep(minuteStep)

  useEffect(() => {
    if (!open) return
    const close = (ev: MouseEvent) => {
      if (root.current && !root.current.contains(ev.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  /* Ora de sub tastatura se aduce in dreptul ochilor - lista are 48 de randuri
     si 22:00 ar sta altfel mult sub marginea de jos. `scrollIntoView` nu exista
     in jsdom, unde ruleaza probele, deci se cheama numai daca e acolo. */
  useEffect(() => {
    if (!open) return
    const row = list.current?.querySelector('li.active')
    if (row instanceof HTMLElement && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' })
    }
  }, [open, marked])

  const show = () => {
    setMarked(nearest(times, value))
    setOpen(true)
  }

  const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (ev.key === 'ArrowDown') {
        ev.preventDefault()
        show()
      }
      return
    }
    if (ev.key === 'Escape' || ev.key === 'Tab') {
      if (ev.key === 'Escape') ev.preventDefault()
      setOpen(false)
      return
    }
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault()
      setMarked((at) => Math.min(times.length - 1, Math.max(0, at + (ev.key === 'ArrowDown' ? 1 : -1))))
      return
    }
    if (ev.key === 'Enter') {
      /* Enter alege ora, nu trimite formularul din spate. */
      ev.preventDefault()
      onChange(times[marked])
      setOpen(false)
    }
  }

  return (
    <div className="input-icon" ref={root} style={{ position: 'relative' }}>
      <i className={icon} style={ICON_STYLE} onClick={show} aria-hidden="true"></i>
      <input
        className={className ? `form-control ${className}` : 'form-control'}
        type="text"
        id={id}
        name={name}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={INPUT_ICON_PAD}
        onChange={(ev) => onChange(ev.target.value)}
        onKeyDown={onKeyDown}
        onClick={show}
      />
      {open && (
        <ul
          ref={list}
          className="dropdown-menu"
          role="listbox"
          style={{ display: 'block', position: 'absolute', top: '100%', left: 0, zIndex: 20, minWidth: '100%', maxHeight: 220, overflowY: 'auto' }}
        >
          {times.map((time, i) => (
            <li key={time} className={`${time === value ? 'selected' : ''}${i === marked ? ' active' : ''}`.trim() || undefined}>
              {/* `onMouseDown` oprit: altfel casuta pierde focusul inainte de
                  clic, `mousedown`-ul de pe document inchide lista, si clicul
                  cade in gol - exact "nu se intampla nimic". */}
              <a
                role="option"
                aria-selected={time === value}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => {
                  onChange(time)
                  setOpen(false)
                }}
              >
                <span className="text">{time}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}
